import { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';

export interface TankCanvasHandle {
  setAngle: (deg: number) => void;
}

interface Props {
  width: number;
  height: number;
}

const TURRET_URL =
  'https://public.readdy.ai/ai/img_res/491b2f7c-1cb9-44dc-b163-eb16ec5b6d99.png';

const TankCanvas = forwardRef<TankCanvasHandle, Props>(({ width, height }, ref) => {
  const canvasRef  = useRef<HTMLCanvasElement>(null);
  const angleRef   = useRef(0);
  const targetRef  = useRef(0);
  const rafRef     = useRef(0);
  const imgRef     = useRef<HTMLImageElement | null>(null);
  const loadedRef  = useRef(false);
  const wRef       = useRef(width);
  const hRef       = useRef(height);

  useEffect(() => {
    wRef.current = width;
    hRef.current = height;
  }, [width, height]);

  useImperativeHandle(ref, () => ({
    setAngle: (deg: number) => {
      targetRef.current = (deg * Math.PI) / 180 + Math.PI;
    },
  }), []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = TURRET_URL;
    img.onload = () => { imgRef.current = img; loadedRef.current = true; };

    const loop = () => {
      const w  = wRef.current;
      const h  = hRef.current;
      const cx = w / 2;
      const cy = h / 2;

      ctx.clearRect(0, 0, w, h);

      // Плавная интерполяция угла
      let diff = targetRef.current - angleRef.current;
      while (diff >  Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      angleRef.current += diff * 0.10;

      // Башня
      if (loadedRef.current && imgRef.current) {
        const image = imgRef.current;
        const scale = Math.min(w / image.naturalWidth, h / image.naturalHeight) * 0.92;
        const dw    = image.naturalWidth  * scale;
        const dh    = image.naturalHeight * scale;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(angleRef.current);
        ctx.drawImage(image, -dw / 2, -dh / 2, dw, dh);
        ctx.restore();
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ display: 'block', width: '100%', height: 'auto' }}
    />
  );
});

TankCanvas.displayName = 'TankCanvas';
export default TankCanvas;
